<template>
  <q-btn
    flat
    dense
    round
    color="white"
    :icon="symRoundedNotifications"
    :class="{
      'notification-btn--pulse': hasMandatory,
      'notification-btn--active': unreadCount > 0,
      'notification-btn--mandatory': hasMandatory,
    }"
    :aria-label="ariaLabel"
    @click="handleClick">
    <!-- Unread count badge -->
    <q-badge
      v-if="unreadCount > 0"
      color="info"
      text-color="white"
      floating
      rounded>
      {{ displayCount }}
    </q-badge>

    <!-- Real-time status indicator -->
    <q-badge
      v-if="!realTimeConnected && unreadCount === 0"
      color="grey-6"
      floating
      rounded
      class="notification-btn__offline-indicator">
      <q-icon
        :name="symRoundedWifiOff"
        size="10px" />
    </q-badge>

    <q-tooltip>
      <div v-if="unreadCount > 0">
        {{ t('title') }}
        <br />
        {{ unreadCount }} {{ unreadCount === 1 ? 'notification' : 'notifications' }}
        <span
          v-if="hasMandatory"
          class="text-warning">
          <br />
          {{ t('tabs.mandatory') }}
        </span>
      </div>
      <div v-else>
        {{ t('title') }}
      </div>

      <div
        v-if="!realTimeConnected"
        class="text-grey-4 q-mt-xs">
        {{ t('realtime.disconnected') }}
      </div>
    </q-tooltip>
  </q-btn>
</template>

<script setup lang="ts">
import { symRoundedNotifications, symRoundedWifiOff } from '@quasar/extras/material-symbols-rounded'
import { useCompositeI18n } from 'src/CrudManager/Core/composables/useCompositeI18n'
import { computed, onMounted } from 'vue'
import { useNotificationStore } from '../stores/useNotificationStore'

type Emits = (e: 'click') => void

const emit = defineEmits<Emits>()
const { t } = useCompositeI18n('core.NotificationCenter')

const store = useNotificationStore()

const unreadCount = computed(() => store.unreadCount)
const hasMandatory = computed(() => store.hasUnreadMandatory)
const realTimeConnected = computed(() => store.realTimeEnabled)

// Icon-only control: without a name it announces as its badge count, or as
// nothing at all when the badge is absent. The tooltip is not read out.
const ariaLabel = computed(() => {
  const count = unreadCount.value
  if (count === 0) return t('title')
  return `${t('title')}, ${count === 1 ? t('subtitle.one') : t('subtitle.many', { count })}`
})

const displayCount = computed(() => {
  if (unreadCount.value > 99) return '99+'
  return unreadCount.value.toString()
})

const handleClick = () => {
  emit('click')
}

// Initialize notifications when button mounts
onMounted(() => {
  store.initialize()
})
</script>

<style scoped>
/* Three beats, then still: an alarm that never stops is furniture, and the
   count badge keeps saying what is waiting long after the motion has done
   its job. Motion-averse users get the badge alone. */
.notification-btn--pulse {
  animation: notification-pulse 2s ease-out 3;
}

.notification-btn--active {
  position: relative;
}

.notification-btn--mandatory {
  position: relative;
}

.notification-btn__offline-indicator {
  right: -2px;
  top: -2px;
}

@keyframes notification-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(49, 204, 236, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(49, 204, 236, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(49, 204, 236, 0);
  }
}

.body--dark .notification-btn--pulse {
  animation: notification-pulse-dark 2s ease-out 3;
}

@keyframes notification-pulse-dark {
  0% {
    box-shadow: 0 0 0 0 rgba(49, 204, 236, 0.5);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(49, 204, 236, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(49, 204, 236, 0);
  }
}
@media (prefers-reduced-motion: reduce) {
  .notification-btn--pulse,
  .body--dark .notification-btn--pulse {
    animation: none;
  }
}
</style>
